import { useId, useState, useRef, useEffect } from "react";
import CheckboxField from "../ui/CheckboxField";

export default function MultiDropdown({
  label,
  name,
  options = [],
  placeholder = "Select options",
  value = [],
  onChange,
  required,
  error,
  showError = true,
  setValue,
  watch,
  maxSelections,
}) {
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Use watch if provided (react-hook-form), otherwise use value prop
  // Always normalise to array — guard against a plain string during role-switch render cycles
  const rawValue = watch ? watch(name) : value;
  const selectedValues = Array.isArray(rawValue)
    ? rawValue
    : rawValue != null && rawValue !== ""
      ? [rawValue]
      : [];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (optionValue) => {
    const current = Array.isArray(selectedValues) ? selectedValues : [];
    let updated;
    if (current.includes(optionValue)) {
      updated = current.filter((v) => v !== optionValue);
    } else if (maxSelections === 1) {
      updated = [optionValue];
    } else if (typeof maxSelections === "number" && current.length >= maxSelections) {
      updated = current;
    } else {
      updated = [...current, optionValue];
    }

    if (setValue) {
      setValue(name, updated, { shouldDirty: true });
    }
    if (onChange) {
      onChange(updated);
    }
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    handleToggle(optionValue);
  };

  const getLabel = (val) => {
    const opt = options.find((o) => o.value === val);
    return opt ? opt.label : val;
  };

  return (
    <div ref={dropdownRef} className="relative">
      <label htmlFor={id} className="block text-sm font-medium text-palette-gray-primary">
        {label} {required && <span className="text-palette-rose-badge">*</span>}
      </label>

      <div className="mt-2">
        <div
          id={id}
          onClick={() => setIsOpen(!isOpen)}
          className="w-full rounded-xl border border-palette-gray-divider px-4 py-3 outline-none focus-within:ring-2 focus-within:ring-palette-indigo-label bg-white text-palette-gray-primary cursor-pointer min-h-[48px] flex flex-wrap items-center gap-1.5"
        >
          {selectedValues.length === 0 ? (
            <span className="text-palette-gray-placeholder">{placeholder}</span>
          ) : (
            selectedValues.map((val) => (
              <span
                key={val}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-palette-indigo-muted text-palette-indigo-heading"
              >
                {getLabel(val)}
                <button
                  type="button"
                  onClick={(e) => handleRemove(val, e)}
                  className="ml-0.5 text-palette-indigo-label hover:text-palette-indigo-text"
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-palette-gray-divider rounded-xl shadow-lg max-h-60 overflow-auto">
            {options.length === 0 ? (
              <div className="px-4 py-3 text-sm text-palette-gray-placeholder">
                No options available
              </div>
            ) : (
              options.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                const selectionLimitReached =
                  typeof maxSelections === "number" &&
                  maxSelections > 0 &&
                  !isSelected &&
                  selectedValues.length >= maxSelections;

                return (
                  <div
                    key={option.value}
                    className={`px-4 py-1.5 ${
                      selectionLimitReached
                        ? "cursor-not-allowed bg-palette-gray-soft text-palette-gray-placeholder"
                        : "hover:bg-palette-gray-soft cursor-pointer"
                    }`}
                  >
                    <CheckboxField
                      label={option.label}
                      checked={isSelected}
                      onChange={() => handleToggle(option.value)}
                      disabled={selectionLimitReached}
                    />
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {typeof maxSelections === "number" && maxSelections > 0 && (
        <p className="mt-1 text-xs text-palette-gray-secondary">
          Select up to {maxSelections} clinic{maxSelections === 1 ? "" : "s"}.
        </p>
      )}
      {showError && error && <p className="mt-1 text-sm text-palette-rose-badge">{error}</p>}
    </div>
  );
}
